<script lang="ts">
  import { onDestroy } from 'svelte';
  import { api } from '../../lib/api';
  import type { Overview } from './types';

  let { data, reload }: { data: Overview; reload: () => void } = $props();
  type Sys = { cpuPct: number; perCore: number[]; load: number[]; memUsedPct: number; memTotalMb: number; uptimeSec: number; gpus: Array<{ name: string; utilPct: number; encoderPct: number | null; memUsedMb: number; memTotalMb: number }> };
  let sys = $state<Sys | null>(null);
  const load = async () => (sys = await api.get<Sys>('/api/admin/system').catch(() => sys));
  void load();
  const t = setInterval(load, 2000);
  onDestroy(() => clearInterval(t));

  const srcName = (id: string) => data.sources.find((s) => s.id === id)?.name ?? id;
  const posName = (id: string | null) => (id ? data.positions.find((p) => p.id === id)?.name ?? id : '—');
  const fmtDb = (db: number | null) => (db === null || db < -90 ? '−∞' : `${db.toFixed(0)} dB`);

  async function kick(id: string) {
    await api.post(`/api/admin/participants/${id}/kick`);
    reload();
  }
</script>

<div class="admin-grid">
  <section class="panel">
    <h3>Host</h3>
    {#if sys}
      <div>CPU {sys.cpuPct}% <span class="muted">(load {sys.load.join(' / ')})</span></div>
      <div class="cores">{#each sys.perCore as c, i (i)}<div class="bar" title="core {i}: {c}%"><div style:height="{c}%"></div></div>{/each}</div>
      <div>Memory {sys.memUsedPct}% of {Math.round(sys.memTotalMb / 1024)} GB</div>
      {#each sys.gpus as g (g.name)}<div>GPU {g.name}: {g.utilPct}% · encoder {g.encoderPct ?? '–'}% · {g.memUsedMb}/{g.memTotalMb} MB</div>{/each}
      <div class="muted">Uptime {Math.floor(sys.uptimeSec / 3600)}h {Math.floor((sys.uptimeSec % 3600) / 60)}m</div>
    {/if}
    <div class="caps">
      Encoders:
      {#each Object.entries(data.caps.encoders) as [name, e] (name)}
        <span class="badge {e.available ? 'ok' : ''}" title={e.reason ?? ''}>{name}</span>
      {/each}
      <span class="badge {data.caps.fallbackSwitch ? 'ok' : 'warn'}">slate</span>
      <span class="badge {data.caps.ndi ? 'ok' : ''}">NDI</span>
      <span class="badge {data.caps.decklink ? 'ok' : ''}">DeckLink</span>
    </div>
  </section>

  <section class="panel">
    <h3>Tally</h3>
    {#each data.tallyStatus as p (p.id)}
      <div class="list-item">
        <span class="badge {p.connected ? 'ok' : 'bad'}">{p.connected ? 'OK' : 'DOWN'}</span><b>{p.name}</b>
        <span class="muted">{p.detail}</span>
        <span class="muted">PGM {p.program.join(',') || '–'} · PVW {p.preview.join(',') || '–'}</span>
      </div>
    {:else}<p class="muted">None configured</p>{/each}
    <h3 style="margin-top:16px">Truck audio</h3>
    <div class="list-item">
      <b>Input</b><span class="badge {data.truck.input.state === 'running' ? 'ok' : data.truck.input.kind === 'none' ? '' : 'bad'}">{data.truck.input.kind} · {data.truck.input.state}</span>
      <span class="muted">{fmtDb(data.truck.input.levelDb)}</span>{#if data.truck.input.lastError}<span class="error">{data.truck.input.lastError}</span>{/if}
    </div>
    <div class="list-item">
      <b>Output</b><span class="badge {data.truck.output.state === 'running' ? 'ok' : !data.truck.output.enabled ? '' : 'bad'}">{data.truck.output.enabled ? data.truck.output.state : 'off'}</span>
      <span class="muted">{fmtDb(data.truck.output.levelDb)} · slots {data.truck.output.slotsUsed}/{data.truck.output.slots}</span>
      {#if data.truck.output.lastError}<span class="error">{data.truck.output.lastError}</span>{/if}
    </div>
  </section>
</div>

<section class="panel" style="margin-top:12px">
  <h3>Pipelines</h3>
  <table>
    <thead><tr><th>Source</th><th>State</th><th>Signal</th><th>Input</th><th>Encode</th><th>Bitrate</th><th>Restarts</th><th>Last error</th></tr></thead>
    <tbody>
      {#each Object.values(data.health) as h (h.sourceId)}
        <tr>
          <td>{srcName(h.sourceId)}</td>
          <td><span class="badge {h.state === 'running' ? 'ok' : h.state === 'backoff' || h.state === 'starting' ? 'warn' : 'bad'}">{h.state}</span></td>
          <td><span class="badge {h.signal ? 'ok' : 'bad'}">{h.signal ? 'OK' : 'NO SIGNAL'}</span>{#if h.input === 'slate'} <span class="muted">slate</span>{/if}</td>
          <td>{h.width}×{h.height} {h.fps}{h.interlaced ? 'i' : 'p'}</td>
          <td>{h.encoder}</td>
          <td>{h.videoKbps} / {h.audioKbps} kbps</td>
          <td>{h.restarts}</td>
          <td class="error small">{h.lastError ?? ''}</td>
        </tr>
      {:else}<tr><td colspan="8" class="muted">No sources running</td></tr>{/each}
    </tbody>
  </table>
</section>

<section class="panel" style="margin-top:12px">
  <h3>Clients ({data.participants.length})</h3>
  <table>
    <thead><tr><th>Name</th><th>Role</th><th>Position</th><th>RTT</th><th>Loss</th><th>Video</th><th>Latency</th><th>Playout</th><th></th></tr></thead>
    <tbody>
      {#each data.participants as p (p.id)}
        <tr>
          <td>{p.talking ? '🎙 ' : ''}{p.calling ? '📣 ' : ''}{p.name}</td>
          <td>{p.role}</td>
          <td>{posName(p.positionId)}{p.lowBandwidth ? ' (low)' : ''}</td>
          <td>{p.stats?.rttMs ?? '–'} ms</td>
          <td>{p.stats?.lossPct ?? '–'}%</td>
          <td>{p.stats?.videoKbps ?? '–'} kbps · {p.stats?.fps ?? '–'} fps</td>
          <td><b>{p.stats?.measuredLatencyMs ?? '–'}</b> ms</td>
          <td>{p.stats?.playoutDelayMs ?? '–'} ms</td>
          <td><button class="small danger" onclick={() => kick(p.id)}>Kick</button></td>
        </tr>
      {:else}<tr><td colspan="9" class="muted">Nobody connected</td></tr>{/each}
    </tbody>
  </table>
</section>

<section class="panel" style="margin-top:12px">
  <h3>Event log</h3>
  <div class="log mono">
    {#each [...data.events].reverse() as e, i (i)}
      <div class="ev {e.level}"><span class="muted">{new Date(e.ts).toLocaleTimeString()}</span> [{e.category}] {e.message}</div>
    {/each}
  </div>
</section>

<style>
  .cores { display: flex; gap: 3px; height: 36px; margin: 6px 0; }
  .bar { width: 10px; background: #222; display: flex; align-items: flex-end; border-radius: 2px; overflow: hidden; }
  .bar div { width: 100%; background: var(--accent); }
  .caps { margin-top: 10px; display: flex; gap: 6px; flex-wrap: wrap; align-items: center; }
  .log { max-height: 360px; overflow: auto; font-size: 0.8rem; line-height: 1.5; }
  .ev.warn { color: var(--warn); }
  .ev.error { color: #ff6b6b; }
  .small { font-size: 0.8rem; }
</style>
